/**
 * Suppliers — a read-only dashboard of the FULL global supplier dictionary so
 * Joan can always see which suppliers BLIP captures, search it, verify a bulk
 * import landed, and download the whole table as CSV. The dictionary is
 * cross-client curation data; edits happen through the "Add a supplier" panel on
 * a benchmark report (governed), not here.
 */
import { useEffect, useMemo, useState } from 'react';
import type { SupplierDictionaryEntry } from '@aurora/shared';
import { api, ApiClientError } from '../api/client';
import { SupplierMergeReview } from '../components/SupplierMergeReview';

export function Suppliers(): JSX.Element {
  const [suppliers, setSuppliers] = useState<SupplierDictionaryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let alive = true;
    void (async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.listSuppliers();
        if (alive) setSuppliers(data.suppliers);
      } catch (e) {
        if (alive)
          setError(
            e instanceof ApiClientError
              ? e.message
              : 'Failed to load suppliers',
          );
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return suppliers;
    return suppliers.filter((s) =>
      [s.canonicalName, ...s.aliases, ...s.domains]
        .join(' ')
        .toLowerCase()
        .includes(needle),
    );
  }, [suppliers, q]);

  const download = async (): Promise<void> => {
    setDownloading(true);
    try {
      const res = await api.downloadSuppliersCsv();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'aurora-supplier-dictionary.csv';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="benchmarks">
      <div className="page-head">
        <h1>Suppliers</h1>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void download()}
          disabled={downloading || loading}
        >
          {downloading ? 'Preparing…' : 'Download CSV'}
        </button>
      </div>

      <p className="muted">
        The full supplier dictionary BLIP tracks across every benchmark —{' '}
        {suppliers.length.toLocaleString()} entries. Add or correct suppliers
        from the “Add a supplier” panel on any benchmark report.
      </p>

      <SupplierMergeReview />

      <div className="filters">
        <input
          type="search"
          placeholder="Search name, alias, or website…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {!loading && !error && (
          <span className="muted">
            {filtered.length.toLocaleString()} shown
          </span>
        )}
      </div>

      {error && <div className="form-error">{error}</div>}
      {loading ? (
        <div className="muted centered">Loading suppliers…</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state muted">
          {suppliers.length === 0
            ? 'No suppliers tracked yet.'
            : `No suppliers match “${q}”.`}
        </div>
      ) : (
        <table className="leads-table">
          <thead>
            <tr>
              <th>Supplier Name</th>
              <th>Other Names</th>
              <th>Website(s)</th>
              <th>Type</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.id}>
                <td className="lead-name">{s.canonicalName}</td>
                <td className="muted">{s.aliases.join(', ')}</td>
                <td className="muted">{s.domains.join(', ')}</td>
                <td>{s.supplierType.replace(/_/g, ' ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
